import type { EmailFilter, FamilySummary, NewsletterFilter, Parent, PortalFilter } from "../types";
import { getBackendEmailAssessment } from "./parentStatus";

export const extractParentsFromFamilies = (families: FamilySummary[]): Parent[] => {
  const parentsById = new Map<string, Parent & { familyIds?: string[] }>();

  families.forEach((family) => {
    const familyParents: Parent[] = [
      {
        id: String(family.parent1Id || ""),
        firstName: family.parent1FirstName,
        lastName: family.parent1LastName,
      },
      {
        id: String(family.parent2Id || ""),
        firstName: family.parent2FirstName,
        lastName: family.parent2LastName,
      },
      ...(family.parents || []).map((parent) => ({
        id: String(parent.id || parent.uuid || parent.parentId || ""),
        firstName: parent.firstName,
        lastName: parent.lastName,
        email: parent.email,
        phone: parent.phone,
      })),
    ].filter((parent) => parent.id);

    familyParents.forEach((parent) => {
      const existing = parentsById.get(parent.id) || { id: parent.id };
      parentsById.set(parent.id, {
        ...existing,
        ...parent,
        familyIds: Array.from(new Set([...(existing.familyIds || []), family.id].filter(Boolean))),
      });
    });
  });

  return Array.from(parentsById.values()).sort((a, b) => {
    const last = String(a.lastName || "").localeCompare(String(b.lastName || ""), "nl");
    if (last !== 0) return last;
    return String(a.firstName || "").localeCompare(String(b.firstName || ""), "nl");
  });
};

export const emailStatusForFilter = (parent: Parent): EmailFilter => {
  const backend = getBackendEmailAssessment(parent);
  return backend?.status || "unknown";
};

export const portalStatusForFilter = (parent: Parent): PortalFilter => {
  if (!parent.accountEnabled) return "inactive";
  if (parent.emailVerified) return "active";
  return "pending";
};

export const newsletterStatusForFilter = (parent: Parent): NewsletterFilter => {
  if (parent.newsletterOptOut) return "opted_out";
  if (parent.newsletterStatus === "SUBSCRIBED") return "subscribed";
  if (parent.newsletterStatus === "UNSUBSCRIBED") return "unsubscribed";
  return "ineligible";
};

export const filterParents = (
  parents: Parent[],
  searchQuery: string,
  emailFilters: EmailFilter[],
  portalFilters: PortalFilter[],
  newsletterFilters: NewsletterFilter[]
): Parent[] => {
  const query = searchQuery.trim().toLowerCase();
  return parents.filter((parent) => {
    const haystack = `${parent.firstName ?? ""} ${parent.lastName ?? ""}`.toLowerCase();
    return (
      (!query || haystack.includes(query)) &&
      (!emailFilters.length || emailFilters.includes(emailStatusForFilter(parent))) &&
      (!portalFilters.length || portalFilters.includes(portalStatusForFilter(parent))) &&
      (!newsletterFilters.length || newsletterFilters.includes(newsletterStatusForFilter(parent)))
    );
  });
};
